import React from 'react';
import request from 'superagent';
import './../ff001-starstruck-lines/ff001-starstruck.css';
import { wrap } from '../../helpers';
import { Reel } from '../reel/reel';
import CoinSpins from '../ff001-starstruck-lines/coin-spins/coin-spins';
import { BigWin } from '../big-win/big-win';
import {
  fromNativeScenarioQueue,
  getScenarioLabel,
  getVisibleWindow,
  isCoinSpinsEligible,
  reconcileCoinSpinsEligibility,
  seedCoinSpinWindows,
  TFf035EditablePlay,
  TFf035Scenario,
  toNativeScenarioQueue,
} from './helpers';

type TVolatility = 0 | 1;
type TPreset = {
  _id: string;
  scenarios: TFf035Scenario[];
  gameInput?: { volatility?: TVolatility };
};

const volatilityLabel = (value: TVolatility) =>
  value === 0 ? 'Thrill (high)' : 'Smooth (low)';

export const Model = ({ modelId }: { modelId: string }) => {
  const [config, setConfig] = React.useState<any>();
  const [plays, setPlays] = React.useState<TFf035EditablePlay[]>([]);
  const [volatility, setVolatility] = React.useState<TVolatility>(0);
  const [presets, setPresets] = React.useState<TPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = React.useState('');
  const [userId, setUserId] = React.useState(localStorage.getItem('userid') || '');

  const effectiveConfig = React.useMemo(() => {
    const setup = config?.volatilitySetups?.[volatility];
    return config && setup ? { ...config, ...setup } : config;
  }, [config, volatility]);

  const blankScenario = React.useCallback(
    () => ({ reelIndexes: effectiveConfig.reelsSetup.map(() => 0) }),
    [effectiveConfig],
  );

  React.useEffect(() => {
    Promise.all([
      request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get-config`)
        .withCredentials().send({ modelId }),
      request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/get`)
        .withCredentials().send({ modelId }),
    ]).then(([configRes, scenariosRes]) => {
      const loadedConfig = configRes.body.config;
      setConfig(loadedConfig);
      setPresets(scenariosRes.body.scenarios || []);
      setPlays([{ reelIndexes: loadedConfig.reelsSetup.map(() => 0) }]);
    }).catch(console.error);
  }, [modelId]);

  const changeVolatility = (next: TVolatility) => {
    setVolatility(next);
    setPlays(current => current.map(play => ({
      reelIndexes: play.reelIndexes.map(() => 0),
    })));
  };

  const setReelIndex = (playIndex: number, reelIndex: number, value: number) => {
    const play = plays[playIndex];
    const strips = play?.reelSet === 'freespins'
      ? effectiveConfig.freeSpinsReels
      : effectiveConfig.baseSpinsReels;
    const strip = strips[reelIndex];
    const wrapped = wrap(value, strip);
    const normalized = wrapped < 0 ? strip.length + wrapped : wrapped;
    setPlays(current => current.map((play, index) => {
      if (index !== playIndex) return play;
      return reconcileCoinSpinsEligibility({
        ...play,
        reelIndexes: play.reelIndexes.map((old, reel) => reel === reelIndex ? normalized : old),
      }, effectiveConfig);
    }));
  };

  const payload = () => ({
    modelId,
    scenarios: toNativeScenarioQueue(plays),
    gameInput: { volatility },
  });

  const save = async () => {
    const res = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/upsert`)
      .withCredentials().send({ ...payload(), _id: selectedPreset || undefined })).body;
    if (!res.success) alert('Error saving scenario');
  };

  const play = async () => {
    if (!userId.trim()) return alert('Please enter a user id');
    const res = (await request.post(`${process.env.REACT_APP_API_URL}/n/slots/scenario/play-user`)
      .withCredentials().send({
        ...payload(),
        gameId: modelId,
        operatorUserId: userId,
        userPublicKey: userId,
    })).body;
    if (!res.success) return alert(res.error?.message || 'Error queueing scenario');
  };

  const selectPreset = (id: string) => {
    setSelectedPreset(id);
    const preset = presets.find(item => item._id === id);
    if (!preset) return;
    setVolatility(preset.gameInput?.volatility ?? 0);
    setPlays(fromNativeScenarioQueue(JSON.parse(JSON.stringify(preset.scenarios))));
  };

  if (!effectiveConfig || plays.length === 0) return <>Loading</>;

  return (
    <div className="ff001-container">
      <h1>{modelId}</h1>
      <h3>Volatility</h3>
      <select value={volatility} onChange={event => changeVolatility(Number(event.target.value) as TVolatility)}>
        <option value={0}>{volatilityLabel(0)}</option>
        <option value={1}>{volatilityLabel(1)}</option>
      </select>
      <div className="scenario-presets">
        <h3>Scenario presets</h3>
        <select value={selectedPreset} onChange={event => selectPreset(event.target.value)}>
          <option value="" />
          {presets.map(preset => (
            <option key={preset._id} value={preset._id}>
              {`${volatilityLabel(preset.gameInput?.volatility ?? 0)}, ${getScenarioLabel(
                fromNativeScenarioQueue(preset.scenarios),
                config?.volatilitySetups?.[preset.gameInput?.volatility ?? 0]
                  ? { ...config, ...config.volatilitySetups[preset.gameInput?.volatility ?? 0] }
                  : config,
              )}`}
            </option>
          ))}
        </select>
      </div>
      {plays.map((play, playIndex) => {
        const reelStrips = play.reelSet === 'freespins'
          ? effectiveConfig.freeSpinsReels
          : effectiveConfig.baseSpinsReels;
        const window = getVisibleWindow(play.reelIndexes, effectiveConfig, play.reelSet);
        const eligible = isCoinSpinsEligible(window, effectiveConfig);
        return (
        <div className={`spin ${play.reelSet === 'freespins' ? 'spin-freespins' : ''}`} key={playIndex}>
          <div className="ff035-play-summary">
            {`Play ${playIndex + 1} · ${getScenarioLabel([play], effectiveConfig)}`}
          </div>
          <div className="ff035-reel-set">
            <span>Reel source</span>
            <select
              aria-label={`Reel source for play ${playIndex}`}
              value={play.reelSet ?? 'base'}
              onChange={event => setPlays(current => current.map((currentPlay, index) => {
                if (index !== playIndex) return currentPlay;
                return {
                  reelIndexes: effectiveConfig.reelsSetup.map(() => 0),
                  ...(event.target.value === 'freespins'
                    ? { reelSet: 'freespins' as const }
                    : {}),
                };
              }))}
            >
              <option value="base">Base reels</option>
              <option value="freespins">Free Spin reels</option>
            </select>
          </div>
          <BigWin
            config={effectiveConfig}
            reelStrips={reelStrips}
            rowsByReel={effectiveConfig.reelsSetup}
            reelIndexes={play.reelIndexes}
            mode={play.reelSet === 'freespins' ? 'Free Spins' : 'Base'}
            showFreespins={play.reelSet !== 'freespins'}
            onApply={reelIndexes => setPlays(current => current.map((currentPlay, index) =>
              index === playIndex
                ? reconcileCoinSpinsEligibility({ ...currentPlay, reelIndexes }, effectiveConfig)
                : currentPlay))}
          />
          <div className="reelContainer">
            {play.reelIndexes.map((reelIndex, index) => (
              <Reel
                key={`${playIndex}-${index}`}
                index={index}
                rows={effectiveConfig.reelsSetup[index]}
                reelIndex={reelIndex}
                reelStrips={reelStrips[index]}
                setReelIndex={({ reelIndex: reel, dir, forcedIndex }) =>
                  setReelIndex(playIndex, reel,
                    typeof forcedIndex === 'number' ? forcedIndex : play.reelIndexes[reel] + (dir || 1))}
              />
            ))}
          </div>
          {eligible && (
            <label>
              <input
                type="checkbox"
                aria-label={`Trigger Coin Spins for play ${playIndex}`}
                checked={play.forceCoinSpinsTrigger === true}
                onChange={event => setPlays(current => current.map((currentPlay, index) =>
                  index !== playIndex ? currentPlay : event.target.checked
                    ? {
                      ...currentPlay,
                      coinSpins: seedCoinSpinWindows(window, effectiveConfig),
                      forceCoinSpinsTrigger: true,
                    }
                    : (() => {
                      const {
                        coinSpins: _removedCoinSpins,
                        forceCoinSpinsTrigger: _removedForce,
                        ...ordinaryPlay
                      } = currentPlay;
                      return ordinaryPlay;
                    })()))}
              />
              Trigger Coin Spins
            </label>
          )}
          <CoinSpins
            window={window}
            coinSpinsWindow={play.coinSpins || []}
            onCoinSpinsWindowsUpdated={coinSpins =>
              setPlays(current => current.map((currentPlay, index) =>
                index === playIndex ? { ...currentPlay, coinSpins } : currentPlay))}
            config={effectiveConfig}
          />
        </div>
      )})}
      <div className="btn-freespins-group">
        <div className="btn-freespins-action" onClick={() => setPlays(value => [...value, blankScenario()])}>Add play</div>
        <div className="btn-freespins-action" onClick={() => setPlays(value => value.length > 1 ? value.slice(0, -1) : value)}>Remove play</div>
      </div>
      <div className="ff001-userid">
        <h3>User Public Key</h3>
        <input value={userId} onChange={event => {
          setUserId(event.target.value);
          localStorage.setItem('userid', event.target.value);
        }} />
      </div>
      <p>A queued scenario is replaced by a newer one. If the game explicitly requests another volatility, this scenario is discarded.</p>
      <div className="model-buttons">
        <div className="btn-save" onClick={() => { setSelectedPreset(''); setPlays([blankScenario()]); }}>New</div>
        <div className="btn-save" onClick={save}>Save</div>
        <div className="btn-save btn-play" onClick={play}>Play</div>
      </div>
    </div>
  );
};

export default Model;
